import { gql } from '@apollo/client';

const PATIENT_FRAGMENT = gql`
    fragment AllVisitsPatient on Patient {
        id
        fullName
    }
`;

const ADDRESS_FRAGMENT = gql`
    fragment AllVisitsAddress on Address {
        id
        line1
        line2
        city
        state
        zipCode
    }
`;

const ORDER_FRAGMENT = gql`
    fragment AllVisitsOrder on Order {
        id
        orderType
        maxActiveOccurrences
        minActiveOccurrences
        numActiveOccurrences
        discontinuedAt
        frequency {
            intervalCount
            intervalUnit
            numVisitsPerInterval
        }
    }
`;

const VISIT_REQUEST_COMMON_FIELDS = gql`
    fragment VisitRequestCommonFields on VisitRequest {
        id
        fulfillmentWindow {
            startDate
            endDate
        }
        timezone
        appointments {
            id
            services {
                id
                name
            }
            clinician {
                id
                fullName
            }
        }
        priority
        workflowStep
        tentativeDate
        patient {
            ...AllVisitsPatient
        }
        address {
            ...AllVisitsAddress
        }
        order {
            ...AllVisitsOrder
        }
    }

    ${PATIENT_FRAGMENT}
    ${ADDRESS_FRAGMENT}
    ${ORDER_FRAGMENT}
`;

const VISIT_COMMON_FIELDS = gql`
    fragment VisitCommonFields on Visit {
        id
        priority: effectivePriority
        effectiveDate
        fulfillmentWindow {
            asDateWindow {
                startDate
                endDate
            }
        }
        timezone
        outcome
        state
        clinicianAppointments {
            services {
                id
                name
            }
            clinician {
                id
                fullName
            }
        }
        services {
            id
            name
        }
        clinicians: assignedClinicians {
            id
            fullName
        }
        patient {
            ...AllVisitsPatient
        }
        address {
            ...AllVisitsAddress
        }
        order {
            ...AllVisitsOrder
        }
    }

    ${PATIENT_FRAGMENT}
    ${ADDRESS_FRAGMENT}
    ${ORDER_FRAGMENT}
`;

export const ALL_VISITS_BY_STATE = gql`
    query AllVisitsByState($filters: VisitFilter, $pagination: PaginationInput!) {
        visits(pagination: $pagination, filters: $filters) {
            totalCount
            items {
                ...VisitCommonFields
            }
        }
    }

    ${VISIT_COMMON_FIELDS}
`;

export const ALL_VISIT_COUNTS = gql`
    query AllVisitCounts(
        $isBulkEditVisits: Boolean!
        $needsServices: VisitRequestFilter
        $needsClinician: VisitRequestFilter
        $needsDate: VisitRequestFilter
        $ready: VisitRequestFilter
        $needsServicesVisits: VisitFilter
        $needsClinicianVisits: VisitFilter
        $needsDateVisits: VisitFilter
        $unscheduled: VisitFilter
        $scheduled: VisitFilter
        $confirmed: VisitFilter
        $enRoute: VisitFilter
        $atLocation: VisitFilter
        $inReview: VisitFilter
        $ended: VisitFilter
    ) {
        needsServices: visitRequests(filters: $needsServices) {
            totalCount
        }
        needsClinician: visitRequests(filters: $needsClinician) {
            totalCount
        }
        needsDate: visitRequests(filters: $needsDate) {
            totalCount
        }
        ready: visitRequests(filters: $ready) {
            totalCount
        }
        needsServicesVisits: visits(filters: $needsServicesVisits) @include(if: $isBulkEditVisits) {
            totalCount
        }
        needsClinicianVisits: visits(filters: $needsClinicianVisits)
            @include(if: $isBulkEditVisits) {
            totalCount
        }
        needsDateVisits: visits(filters: $needsDateVisits) @include(if: $isBulkEditVisits) {
            totalCount
        }
        unscheduled: visits(filters: $unscheduled) {
            totalCount
        }
        scheduled: visits(filters: $scheduled) {
            totalCount
        }
        confirmed: visits(filters: $confirmed) {
            totalCount
        }
        enRoute: visits(filters: $enRoute) {
            totalCount
        }
        atLocation: visits(filters: $atLocation) {
            totalCount
        }
        inReview: visits(filters: $inReview) {
            totalCount
        }
        ended: visits(filters: $ended) {
            totalCount
        }
    }
`;

export const ALL_VISIT_REQUESTS_NEEDING_ASSIGNMENT = gql`
    query AllNeedingAssignment($filters: VisitRequestFilter, $pagination: PaginationInput!) {
        visitRequests(pagination: $pagination, filters: $filters) {
            totalCount
            items {
                ...VisitRequestCommonFields
                # disciplines {
                #     services {
                #         items {
                #             id
                #             name
                #         }
                #     }
                # }
                # decisionSupport {
                #     id
                #     recommendations: clinicianRecommendations {
                #         clinicians: recommendedClinician {
                #             clinician {
                #                 id
                #                 fullName
                #             }
                #         }
                #     }
                # }
            }
        }
    }

    ${VISIT_REQUEST_COMMON_FIELDS}
`;

export const ALL_NEEDING_ATTENTION = gql`
    query AllNeedingAttention(
        $visitFilters: VisitFilter
        $visitRequestFilters: VisitRequestFilter
        $visitPagination: PaginationInput!
        $visitRequestPagination: PaginationInput!
    ) {
        visits(pagination: $visitPagination, filters: $visitFilters) {
            totalCount
            items {
                ...VisitCommonFields
                warnings {
                    type
                }
            }
        }
        visitRequests(pagination: $visitRequestPagination, filters: $visitRequestFilters) {
            totalCount
            items {
                ...VisitRequestCommonFields
                # disciplines {
                #     services {
                #         items {
                #             id
                #             name
                #         }
                #     }
                # }
                # decisionSupport {
                #     id
                #     recommendations: clinicianRecommendations {
                #         clinicians: recommendedClinician {
                #             clinician {
                #                 id
                #                 fullName
                #             }
                #         }
                #     }
                # }
                warnings {
                    type
                }
            }
        }
    }

    ${VISIT_REQUEST_COMMON_FIELDS}
    ${VISIT_COMMON_FIELDS}
`;
